import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/productsAll.css';
import ProductsGrid from '../components/ProductsGrid';
import ProductFilters from '../components/ProductFilters';
import PremiumCarousel from '../components/PremiumCarousel';
import Cookies from 'js-cookie';
import config from '../config';
import Notification from '../components/Notification';
import 'swiper/swiper-bundle.css';
import { ProductGridSkeleton, CarouselSkeleton } from '../components/Skeletons';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { handleLikeClick as likeHandler } from '../utils/likeHandler';
import PerformanceIndicator from '../components/PerformanceIndicator';
import { PerformanceToggle } from '../components/PerformanceDashboard';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import ProductService from '../services/productService';
import { CityContext } from '../contexts/CityContext';
import { CategoryContext } from '../contexts/CategoryContext';
import { UserContext } from '../contexts/UserContext';
import { filterActivePromotions } from '../utils/promotionUtils';

const ProductsAll = () => {
    const [products, setProducts] = useState([]);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const navigate = useNavigate();
    const { categoryList } = useContext(CategoryContext); // Add CategoryContext
    const { redirectToLogin } = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [likedProducts, setLikedProducts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    
    // Filter state
    const [filters, setFilters] = useState({
        sortBy: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        city: ''
    });
    const [allProducts, setAllProducts] = useState([]); // Store unfiltered products
    const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products
    const [promotedProducts, setPromotedProducts] = useState([]); // Store promoted products
    const [promotedLoading, setPromotedLoading] = useState(false);
    
    // View mode state - category or list
    const [viewMode, setViewMode] = useState('category');
    
    // Performance monitoring
    const { startTiming, endTiming, recordPageLoad } = usePerformanceMonitoring();
    const [performanceData, setPerformanceData] = useState({ loadTime: 0, fromCache: false, itemCount: 0 });

    // Filter products based on current filters
    const applyFilters = useCallback((productsToFilter, currentFilters) => {
        let filtered = [...productsToFilter];

        // Condition filter
        if (currentFilters.condition) {
            filtered = filtered.filter(product => 
                product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
            );
        }

        // Price range filter
        if (currentFilters.minPrice || currentFilters.maxPrice) {
            filtered = filtered.filter(product => {
                const price = parseFloat(product.price) || 0;
                const min = parseFloat(currentFilters.minPrice) || 0;
                const max = parseFloat(currentFilters.maxPrice) || Infinity;
                return price >= min && (max === Infinity || price <= max);
            });
        }

        // City filter
        if (currentFilters.city) {
            filtered = filtered.filter(product => 
                product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
            );
        }

        // Sort products
        if (currentFilters.sortBy) {
            switch (currentFilters.sortBy) {
                case 'newest':
                    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'price_low':
                    filtered.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
                    break;
                case 'price_high':
                    filtered.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
                    break;
                case 'most_liked':
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                case 'most_viewed':
                    filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
                    break;
                default:
                    break;
            }
        }

        return filtered;
    }, []);

    // Handle filter changes
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Apply filters when filters or allProducts change
    useEffect(() => {
        if (allProducts.length > 0) {
            const filtered = applyFilters(allProducts, filters);
            setFilteredProducts(filtered);
            if (viewMode === 'list') {
                setProducts(filtered); // Update products displayed in list view
            } else {
                // Update categoryProducts for category view
                const groupedByCategory = groupProductsByCategory(filtered);
                setCategoryProducts(groupedByCategory);
            }
        }
    }, [filters, allProducts, applyFilters, viewMode]);

    // Get cities from context
    const { cities: contextCities, isLoading: citiesLoading } = useContext(CityContext);

    useEffect(() => {
        const initializeLocations = async () => {
            // Use optimized service method for provinces
            const provincesResult = await ProductService.getPublicProvinces();
            setProvinces(provincesResult.data || []);

            const provinceId = searchParams.get('province');
            const cityId = searchParams.get('city');

            if (provinceId) {
                setSelectedProvince(provinceId);
                // Fetch cities ONLY for the selected province
                const citiesResult = await ProductService.getCitiesByProvince(provinceId);
                const provinceCities = Array.isArray(citiesResult.data) ? citiesResult.data : [];
                
                // Filter context cities to ONLY include cities from this province
                if (contextCities && Array.isArray(contextCities) && contextCities.length > 0) {
                    const filteredContextCities = contextCities.filter(
                        contextCity => contextCity.province_id === parseInt(provinceId) || contextCity.province === parseInt(provinceId)
                    );
                    
                    // Merge API cities with filtered context cities (avoid duplicates)
                    filteredContextCities.forEach(contextCity => {
                        if (!provinceCities.find(apiCity => apiCity.id === contextCity.id)) {
                            provinceCities.push(contextCity);
                        }
                    });
                }
                
                setCities(provinceCities);

                if (cityId && provinceCities.some(city => city.id === parseInt(cityId))) {
                    setSelectedCity(cityId);
                }
            }
        };

        const getInitialProducts = async () => {
            setLoading(true);
            startTiming();
            
            try {
                const provinceId = searchParams.get('province');
                const cityId = searchParams.get('city');
                
                // Create cache key based on filters
                const cacheKey = `wennze_products_all_${provinceId || 'all'}_${cityId || 'all'}`;
                const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
                
                // Try to load from cache first
                try {
                    const cachedData = localStorage.getItem(cacheKey);
                    if (cachedData) {
                        const { data: cachedProducts, timestamp, hasMore: cachedHasMore } = JSON.parse(cachedData);
                        const isValidCache = Date.now() - timestamp < CACHE_TTL;
                        
                        if (isValidCache && Array.isArray(cachedProducts) && cachedProducts.length > 0) {

                            // Use cached data immediately
                            setAllProducts(cachedProducts);
                            setFilteredProducts(cachedProducts);
                            setProducts(cachedProducts);
                            
                            const groupedByCategory = groupProductsByCategory(cachedProducts);
                            setCategoryProducts(groupedByCategory);
                            
                            setHasMore(cachedHasMore);
                            setPage(1);
                            
                            const loadTime = endTiming(true, cachedProducts.length);
                            setPerformanceData({
                                loadTime,
                                fromCache: true,
                                itemCount: cachedProducts.length
                            });
                            
                            setLoading(false);
                            
                            // Refresh in background
                            setTimeout(() => {
                                ProductService.fetchAllProductsFromNetwork(provinceId, cityId, 1, 20)
                                    .then(result => {
                                        const freshData = result.results || [];
                                        if (freshData.length > 0) {
                                            localStorage.setItem(cacheKey, JSON.stringify({
                                                data: freshData,
                                                timestamp: Date.now(),
                                                hasMore: !!result.next
                                            }));
                                        }
                                    })
                                    .catch(err => {});
                            }, 100);
                            
                            return; // Exit early with cached data
                        }
                    }
                } catch (cacheError) {

                }
                
                // Fetch products from the API
                const result = await ProductService.fetchAllProductsFromNetwork(provinceId, cityId, 1, 20);
                const loadTime = endTiming(false, result.results?.length || 0);
                
                // Use the results directly for list view and group for category view
                const allProductsData = result.results || [];
                setAllProducts(allProductsData);
                setFilteredProducts(allProductsData);
                setProducts(allProductsData);
                
                // Group products by category for category view
                const groupedByCategory = groupProductsByCategory(allProductsData);
                setCategoryProducts(groupedByCategory);
                
                setHasMore(!!result.next);
                setPage(1);
                
                setPerformanceData({
                    loadTime,
                    fromCache: false,
                    itemCount: result.results?.length || 0
                });
                
                // Cache the fresh data
                try {
                    localStorage.setItem(cacheKey, JSON.stringify({
                        data: allProductsData,
                        timestamp: Date.now(),
                        hasMore: !!result.next
                    }));
                } catch (cacheError) {

                }
            } catch (error) {
                console.error('Error loading all products:', error);
                endTiming(false, 0, error);
                setProducts([]);
                setHasMore(false);
            } finally {
                setLoading(false);
                const totalPageTime = endTiming(false, 0);
                recordPageLoad('ProductsAll', totalPageTime);
            }
        };

        initializeLocations();
        getInitialProducts();
    }, [searchParams, startTiming, endTiming, recordPageLoad]);

    // Fetch promoted products
    useEffect(() => {
        const fetchPromotedProducts = async () => {
            setPromotedLoading(true);
            try {
                const PROMOTED_CACHE_KEY = 'wennze_products_all_promoted_cache';
                const PROMOTED_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

                try {
                    const cachedData = localStorage.getItem(PROMOTED_CACHE_KEY);
                    if (cachedData) {
                        const { data: cachedPromoted, timestamp } = JSON.parse(cachedData);
                        const isValidCache = Date.now() - timestamp < PROMOTED_CACHE_TTL;

                        if (isValidCache && Array.isArray(cachedPromoted)) {
                            const activeCachedPromoted = filterActivePromotions(cachedPromoted);
                            setPromotedProducts(activeCachedPromoted);
                            setPromotedLoading(false);
                            return;
                        }
                    }
                } catch (cacheError) {

                }

                // Fetch from network
                const result = await ProductService.getPromotedProducts();
                const activePromoted = filterActivePromotions(result.data || []);
                setPromotedProducts(activePromoted);

                // Cache the results
                try {
                    localStorage.setItem(PROMOTED_CACHE_KEY, JSON.stringify({
                        data: activePromoted,
                        timestamp: Date.now()
                    }));
                } catch (cacheError) {

                }
            } catch (error) {
                console.error('Error fetching promoted products:', error);
            } finally {
                setPromotedLoading(false);
            }
        };

        fetchPromotedProducts();
    }, []);

    // Fetch campaigns
    useEffect(() => {
        const fetchCampaigns = async () => {
            const CAMPAIGNS_CACHE_KEY = 'wennze_campaigns_cache_v2';
            const CAMPAIGNS_CACHE_TTL = 60 * 60 * 1000; // 1 hour

            try {
                const cachedData = localStorage.getItem(CAMPAIGNS_CACHE_KEY);
                if (cachedData) {
                    const { data: cachedCampaigns, timestamp } = JSON.parse(cachedData);
                    const isValidCache = Date.now() - timestamp < CAMPAIGNS_CACHE_TTL;
                    
                    if (isValidCache && cachedCampaigns.length > 0) {
                        setCampaigns(cachedCampaigns);
                        return;
                    }
                }

                const response = await fetch(`${config.backendUrl}/campaigns/active/`);
                if (!response.ok) return;
                const data = await response.json();
                
                let campaignsArray = [];
                if (data.results && Array.isArray(data.results)) {
                    campaignsArray = data.results;
                } else if (Array.isArray(data)) {
                    campaignsArray = data;
                }
                
                const formattedCampaigns = campaignsArray.map(campaign => ({
                    imageUrl: campaign.images && campaign.images.length > 0 ? campaign.images[0].image : '',
                    link: campaign.link,
                    caption: { 
                        title: campaign.title || 'Promotion', 
                        text: `Sponsorisé par ${campaign.client_name || 'Wennze'}` 
                    },
                    ownerId: campaign.user
                }));
                
                setCampaigns(formattedCampaigns);
                localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
                    data: formattedCampaigns,
                    timestamp: Date.now()
                }));
            } catch (error) {
                console.error('Failed to fetch campaigns:', error);
            }
        };

        fetchCampaigns();
    }, []);

    // Effect to ensure cities are available from context when they load
    useEffect(() => {
        if (contextCities && contextCities.length > 0 && cities.length === 0 && !selectedProvince) {
            setCities(contextCities);
        }
    }, [contextCities, cities.length, selectedProvince]);

    const handleProvinceChange = async (e) => {
        const provinceId = e.target.value;
        setSelectedProvince(provinceId);
        setSelectedCity('');
        setSearchParams(params => {
            if (provinceId) params.set('province', provinceId);
            else params.delete('province');
            params.delete('city');
            return params;
        });
        
        setLoading(true);
        
        try {
            if (provinceId) {
                // Fetch cities ONLY for the selected province
                const citiesResult = await ProductService.getCitiesByProvince(provinceId);
                const provinceCities = Array.isArray(citiesResult.data) ? citiesResult.data : [];
                
                // Filter context cities to ONLY include cities from this province
                if (contextCities && Array.isArray(contextCities) && contextCities.length > 0) {
                    const filteredContextCities = contextCities.filter(
                        contextCity => contextCity.province_id === parseInt(provinceId) || contextCity.province === parseInt(provinceId)
                    );
                    
                    // Merge API cities with filtered context cities (avoid duplicates)
                    filteredContextCities.forEach(contextCity => {
                        if (!provinceCities.find(apiCity => apiCity.id === contextCity.id)) {
                            provinceCities.push(contextCity);
                        }
                    });
                }
                
                setCities(provinceCities);
            } else {
                // If no province selected, show all cities
                setCities(contextCities || []);
            }
            
            const result = await ProductService.fetchAllProductsFromNetwork(provinceId, '', 1, 20);
            const loadTime = endTiming(false, result.results?.length || 0);
            
            const allProductsData = result.results || [];
            setAllProducts(allProductsData); // Store unfiltered products
            setFilteredProducts(allProductsData); // Initialize filtered products
            setProducts(allProductsData);
            
            // Group products by category for category view
            const groupedByCategory = groupProductsByCategory(allProductsData);
            setCategoryProducts(groupedByCategory);
            
            setHasMore(!!result.next);
            setPage(1);
            
            // Cache the fresh data
            const cacheKey = `wennze_products_all_${provinceId || 'all'}_all`;
            try {
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: allProductsData,
                    timestamp: Date.now(),
                    hasMore: !!result.next
                }));
            } catch (cacheError) {

            }
            
            setPerformanceData({
                loadTime,
                fromCache: false,
                itemCount: result.results?.length || 0
            });
        } catch (error) {
            console.error('Error loading province products:', error);
            endTiming(false, 0, error);
            setProducts([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const handleCityChange = async (e) => {
        const cityId = e.target.value;
        setSelectedCity(cityId);
        setSearchParams(params => {
            if (cityId) params.set('city', cityId);
            else params.delete('city');
            return params;
        });
        
        setLoading(true);
        
        try {
            const result = await ProductService.fetchAllProductsFromNetwork(selectedProvince, cityId, 1, 20);
            const loadTime = endTiming(false, result.results?.length || 0);
            
            const allProductsData = result.results || [];
            setAllProducts(allProductsData); // Store unfiltered products
            setFilteredProducts(allProductsData); // Initialize filtered products
            setProducts(allProductsData);
            
            // Group products by category for category view
            const groupedByCategory = groupProductsByCategory(allProductsData);
            setCategoryProducts(groupedByCategory);
            
            setHasMore(!!result.next);
            setPage(1);
            
            // Cache the fresh data
            const cacheKey = `wennze_products_all_${selectedProvince || 'all'}_${cityId || 'all'}`;
            try {
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: allProductsData,
                    timestamp: Date.now(),
                    hasMore: !!result.next
                }));
            } catch (cacheError) {

            }
            
            setPerformanceData({
                loadTime,
                fromCache: false,
                itemCount: result.results?.length || 0
            });
        } catch (error) {
            console.error('Error loading city products:', error);
            endTiming(false, 0, error);
            setProducts([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchMoreData = async () => {
        if (!hasMore || loadingMore) return;
        
        setLoadingMore(true);
        const nextPage = page + 1;
        
        try {
            let result;
            if (selectedProvince || selectedCity) {
                result = await ProductService.fetchAllProductsFromNetwork(selectedProvince, selectedCity, nextPage, 20);
            } else {
                result = await ProductService.fetchAllProductsFromNetwork('', '', nextPage, 20);
            }
            
            const loadTime = endTiming(false, result.results?.length || 0);
            
            // Append new products to existing ones
            setProducts(prevProducts => {
                const newProducts = result.results || [];
                // Filter out duplicates
                const filteredNewProducts = newProducts.filter(
                    newProduct => !prevProducts.some(existing => existing.id === newProduct.id)
                );
                const updatedProducts = [...prevProducts, ...filteredNewProducts];
                
                // Update category products as well
                const groupedByCategory = groupProductsByCategory(updatedProducts);
                setCategoryProducts(groupedByCategory);
                
                return updatedProducts;
            });
            
            setHasMore(!!result.next);
            setPage(nextPage);
        } catch (error) {
            console.error('Error loading more products:', error);
            endTiming(false, 0, error);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleLikeClick = async (productId, isLikedByUser) => {
        await likeHandler({
            productId,
            isLikedByUser,
            loggedInUser,
            setLikedProducts,
            setProducts,
            navigate,
            setNotification,
            redirectToLogin
        });
    };

    const handleCampaignClick = (campaign) => {
        if (!loggedInUser) {
            // Navigate to login page for non-logged-in users
            redirectToLogin(window.location.pathname);
        } else if (campaign.ownerUuid || campaign.ownerId) {
            // Navigate to user's corner/shop
            navigate(`/user/corner/${campaign.ownerUuid || campaign.ownerId}/`);
        } else if (campaign.link) {
            // Fallback to external link
            window.open(campaign.link, '_blank');
        }
    };

    // Group products by category for category view
    const groupProductsByCategory = (productsList) => {
        const grouped = {};
        productsList.forEach(product => {
            const categoryName = product.category_name || 'Autres';
            if (!grouped[categoryName]) {
                // Try to get category ID from multiple sources
                let categoryId = product.category_id || product.category?.id || product.category;
                
                // If still no ID, try to find it in CategoryContext by name
                if (!categoryId && categoryList && categoryList.length > 0) {
                    const foundCategory = categoryList.find(cat => cat.name === categoryName);
                    categoryId = foundCategory ? foundCategory.id : null;
                }
                
                grouped[categoryName] = {
                    category_name: categoryName,
                    category_id: categoryId,
                    products: []
                };
            }
            grouped[categoryName].products.push(product);
        });
        
        const result = Object.values(grouped);
        
        return result;
    };

    return (
        <div>
            <div className="filters-container">
                
                {/* View Mode Toggle */}
                <div className="view-mode-toggle">
                    <button 
                        className={`view-mode-btn ${viewMode === 'category' ? 'active' : ''}`}
                        onClick={() => setViewMode('category')}
                        title="Vue par catégories"
                    >
                        <i className="fas fa-th"></i>
                        <span>Catégories</span>
                    </button>
                    <button 
                        className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="Vue liste"
                    >
                        <i className="fas fa-list"></i>
                        <span>Liste</span>
                    </button>
                </div>

                <select
                    value={selectedProvince}
                    onChange={handleProvinceChange}
                    className="filter-select"
                    disabled={loading}
                >
                    <option value="">Toutes les provinces</option>
                    {provinces.map(province => (
                        <option key={province.id} value={province.id}>
                            {province.name}
                        </option>
                    ))}
                </select>

                <select
                    value={selectedCity}
                    onChange={handleCityChange}
                    className="filter-select"
                    disabled={!selectedProvince || loading}
                >
                    <option value="">Toutes les villes</option>
                    {cities.length > 0 ? (
                        cities.map(city => (
                            <option key={city.id} value={city.id}>
                                {city.name}
                            </option>
                        ))
                    ) : (
                        <option value="" disabled>Aucune ville disponible</option>
                    )}
                </select>
            </div>

            {/* Add Product Filters */}
            {!loading && allProducts.length > 0 && (
                <ProductFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    productsCount={filteredProducts.length}
                    totalProducts={allProducts.length}
                />
            )}

            {/* Promoted Products Carousel */}
            {promotedLoading ? (
                <CarouselSkeleton />
            ) : promotedProducts.length > 0 && (
                <PremiumCarousel
                    products={promotedProducts}
                    onProductClick={(productUuid) => navigate(`/products/view/${productUuid}`)}  // Use productUuid
                    handleLikeClick={handleLikeClick}
                    loggedInUser={loggedInUser}
                    likedProducts={likedProducts}
                />
            )}

            <div className="products-page-container">
                {loading ? (
                    <>
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="category-section">
                                <ProductGridSkeleton />
                            </div>
                        ))}
                    </>
                ) : viewMode === 'category' ? (
                    // Category View - Products grouped by categories
                    categoryProducts.length > 0 ? (
                        <div className="category-products-container">
                            {categoryProducts.map((categoryGroup, index) => {
                                // Show only first 8 products in category view
                                const displayedProducts = categoryGroup.products.slice(0, 8);
                                const hasMoreProducts = categoryGroup.products.length > 8;
                                
                                return (
                                    <div key={categoryGroup.category_name} className="category-section">
                                        <div className="category-header">
                                            <div className="category-title-wrapper">
                                                <h2>{categoryGroup.category_name}</h2>
                                                <span className="product-count">
                                                    {categoryGroup.products.length} produit{categoryGroup.products.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            {hasMoreProducts && (
                                                <button 
                                                    className="view-more-button"
                                                    onClick={() => navigate(`/products/category/${categoryGroup.category_id}/${encodeURIComponent(categoryGroup.category_name)}`)}
                                                >
                                                    <i className="fas fa-eye"></i>
                                                    <span>Voir plus</span>
                                                </button>
                                            )}
                                        </div>
                                        <ProductsGrid
                                            products={displayedProducts}
                                            likedProducts={likedProducts}
                                            loggedInUser={loggedInUser}
                                            handleProductClick={(productUuid) => navigate(`/products/view/${productUuid}`)}  // Use productUuid
                                            handleLikeClick={handleLikeClick}
                                            hasMore={false}
                                            fetchMoreData={() => {}}
                                            loading={false}
                                            campaigns={campaigns}
                                            onCampaignPress={handleCampaignClick}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-products-message modern-not-found">
                            <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                                <circle cx="32" cy="32" r="32" fill="#f3f4f6"/>
                                <path d="M20 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#c51618" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="26" cy="28" r="2" fill="#c51618"/>
                                <circle cx="38" cy="28" r="2" fill="#c51618"/>
                            </svg>
                            <h2>Aucun produit trouvé</h2>
                            <p>Essayez de modifier vos filtres de recherche.</p>
                        </div>
                    )
                ) : (
                    // List View - All products in one continuous list
                    products.length > 0 ? (
                        <div className="products-section">
                            <ProductsGrid
                                products={products}
                                likedProducts={likedProducts}
                                loggedInUser={loggedInUser}
                                handleProductClick={(productUuid) => navigate(`/products/view/${productUuid}`)}  // Use productUuid
                                handleLikeClick={handleLikeClick}
                                hasMore={hasMore}
                                fetchMoreData={fetchMoreData}
                                loadingMore={loadingMore}
                                campaigns={campaigns}
                                onCampaignPress={handleCampaignClick}
                            />
                        </div>
                    ) : (
                        <div className="modern-not-found">
                            <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                                <circle cx="32" cy="32" r="32" fill="#f3f4f6"/>
                                <path d="M20 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#c51618" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="26" cy="28" r="2" fill="#c51618"/>
                                <circle cx="38" cy="28" r="2" fill="#c51618"/>
                            </svg>
                            <h2>Aucun produit trouvé</h2>
                            <p>Soyez le premier à publier un produit dans cette région !</p>
                            <button
                                className="primary-button"
                                onClick={() => navigate('/add-post')}
                            >
                                Publier un produit
                            </button>
                        </div>
                    )
                )}
            </div>
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}
            <ScrollToTopButton />
            <PerformanceToggle />
        </div>
    );
};

export default ProductsAll;